import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Menu, X, CalendarDays } from 'lucide-react';
import { useState } from 'react';

const Logo = ({ className = "" }) => (
  <Link to="/" className={`flex items-center ${className}`}>
    <span className="text-white font-bold text-2xl tracking-tighter">Jasne</span>
    <span className="text-[#00BFFF] font-bold text-2xl tracking-tighter">Svetla</span>
  </Link>
);

const PrivacyPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#05050A]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Domov</Link>
              <Link to="/blog" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Blog</Link>
              <Link to="/#contact" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Kontakt</Link>
            </nav>
            <Link to="/#booking" className="hidden md:flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-6 py-2.5 font-semibold hover:bg-[#33CCFF] transition-all hover:shadow-[0_0_20px_rgba(0,191,255,0.4)]">
              <CalendarDays size={18} />
              Rezervovať
            </Link>
            <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#05050A]/95 backdrop-blur-xl border-t border-white/10 p-6">
            <nav className="flex flex-col gap-4">
              <Link to="/" className="text-zinc-400 hover:text-white font-medium py-2">Domov</Link>
              <Link to="/blog" className="text-zinc-400 hover:text-white font-medium py-2">Blog</Link>
            </nav>
          </div>
        )}
      </header>

      {/* Content */}
      <article className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="text-[#00BFFF]" size={20} />
            <span className="text-[#00BFFF] text-xs font-bold uppercase tracking-[0.2em]">GDPR</span>
          </div>

          <h1 data-testid="privacy-title" className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-light text-white mb-12">
            Ochrana osobných údajov
          </h1>

          <div className="space-y-10 text-zinc-400 leading-relaxed">

            <section>
              <h2 className="text-xl font-medium text-white mb-4">1. Prevádzkovateľ</h2>
              <p>
                Prevádzkovateľom osobných údajov je <strong className="text-white">JasneSvetla.sk</strong>, so sídlom Šípková 5, 900 27 Bernolákovo, Slovenská republika.
              </p>
              <p className="mt-2">
                Kontakt: <a href="tel:+421915264064" className="text-[#00BFFF]">+421 915 264 064</a>, email: <a href="mailto:m.petor@seznam.cz" className="text-[#00BFFF]">m.petor@seznam.cz</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">2. Aké údaje zbierame</h2>
              <p>Pri využívaní našich služieb môžeme zbierať nasledovné osobné údaje:</p>
              <ul className="mt-3 space-y-2">
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Kontaktný formulár:</strong> meno, emailová adresa, telefónne číslo, text správy</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Rezervačný systém:</strong> meno, emailová adresa, telefónne číslo, typ služby, preferovaný termín</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">AI Chatbot:</strong> obsah konverzácie (anonymne, bez identifikácie používateľa)</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Cookies:</strong> technické cookies pre správne fungovanie webstránky</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">3. Účel spracovania</h2>
              <p>Vaše osobné údaje spracúvame na tieto účely:</p>
              <ul className="mt-3 space-y-2">
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Odpoveď na vaše otázky a požiadavky z kontaktného formulára</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Spracovanie a potvrdenie rezervácií termínov</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Poskytovanie zákazníckeho servisu prostredníctvom AI chatbota</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Zlepšovanie kvality našich služieb</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">4. Právny základ</h2>
              <p>
                Osobné údaje spracúvame na základe:
              </p>
              <ul className="mt-3 space-y-2">
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Súhlas</strong> – pri odoslaní kontaktného formulára alebo rezervácie dobrovoľne poskytujete svoje údaje</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Oprávnený záujem</strong> – pre zlepšovanie služieb a analytiku návštevnosti</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Plnenie zmluvy</strong> – pre realizáciu objednaných služieb</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">5. Doba uchovávania</h2>
              <p>
                Vaše osobné údaje uchovávame len po dobu nevyhnutnú na splnenie účelu spracovania:
              </p>
              <ul className="mt-3 space-y-2">
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Kontaktné správy: 2 roky od doručenia</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Rezervácie: 2 roky od realizácie služby</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Chat konverzácie: 6 mesiacov</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30">Analytické cookies: 1 rok</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">6. Vaše práva</h2>
              <p>V súlade s nariadením GDPR (EÚ) 2016/679 máte právo:</p>
              <ul className="mt-3 space-y-2">
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Právo na prístup</strong> – požiadať o informácie o spracúvaní vašich údajov</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Právo na opravu</strong> – požiadať o opravu nesprávnych údajov</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Právo na vymazanie</strong> – požiadať o vymazanie vašich údajov</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Právo na obmedzenie spracovania</strong></li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Právo na prenosnosť údajov</strong></li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Právo namietať</strong> – proti spracúvaniu na základe oprávneného záujmu</li>
                <li className="pl-4 border-l-2 border-[#00BFFF]/30"><strong className="text-white">Právo podať sťažnosť</strong> – na Úrad na ochranu osobných údajov SR</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">7. Cookies</h2>
              <p>
                Naša webstránka používa technické cookies nevyhnutné pre správne fungovanie stránky. 
                Tieto cookies nezbierajú osobné údaje a sú automaticky vymazané po zatvorení prehliadača alebo po uplynutí platnosti.
              </p>
              <p className="mt-2">
                Používaním stránky a kliknutím na "Súhlasím" v cookie banneri vyjadrujete súhlas s používaním cookies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">8. Bezpečnosť údajov</h2>
              <p>
                Vaše údaje chránime pomocou moderných bezpečnostných opatrení vrátane šifrovania komunikácie (SSL/TLS), 
                bezpečného ukladania dát a obmedzeného prístupu k osobným údajom.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">9. Poskytovanie údajov tretím stranám</h2>
              <p>
                Vaše osobné údaje neposkytujeme tretím stranám na marketingové účely. 
                Údaje môžu byť zdieľané len s poskytovateľmi služieb, ktorí nám pomáhajú prevádzkovať webstránku 
                (hosting, emailové služby), a to výlučne na základe zmluvy o spracúvaní osobných údajov.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium text-white mb-4">10. Kontakt</h2>
              <p>
                V prípade otázok týkajúcich sa ochrany osobných údajov nás kontaktujte:
              </p>
              <div className="mt-4 p-6 bg-white/[0.03] border border-white/10">
                <p className="text-white font-medium">JasneSvetla.sk</p>
                <p className="mt-2">Šípková 5, 900 27 Bernolákovo</p>
                <p>Tel: <a href="tel:+421915264064" className="text-[#00BFFF]">+421 915 264 064</a></p>
                <p>Email: <a href="mailto:m.petor@seznam.cz" className="text-[#00BFFF]">m.petor@seznam.cz</a></p>
              </div>
            </section>

            <p className="text-sm text-zinc-600 pt-6 border-t border-white/10">
              Posledná aktualizácia: Apríl 2026
            </p>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo />
            <div className="flex items-center gap-6 text-zinc-400 text-sm">
              <span>&copy; 2026 JasneSvetla.sk</span>
            </div>
            <div className="flex items-center gap-4 text-zinc-500 text-sm">
              <Link to="/" className="hover:text-[#00BFFF] transition-colors">Domov</Link>
              <span>&bull;</span>
              <Link to="/blog" className="hover:text-[#00BFFF] transition-colors">Blog</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
